import { useQuery } from '@tanstack/react-query';
import { fileGetResult } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseFileDownloadUrlResult {
  downloadUrl: string | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Resolves a short-lived presigned GET url for a produced artifact
 * (`files.id`, e.g. an export/render `job_checkpoints.artifactFileId`) via
 * `file.get` (§5.7/§9.3: "large artifacts never over WS — push a presigned
 * S3 URL"; §9.2 preview contract).
 *
 * OPEN ISSUE: `packages/ws-protocol/src/op-contracts.ts` currently types
 * `'file.get'`'s response as `ExplorerEntry`, but `packages/schemas/src/
 * file.ts`'s own `fileGetResult` (`{ file, downloadUrl }`, its doc comment:
 * "F7 preview pane) — the row plus a short-lived presigned GET url") is the
 * actual paired response shape — the two don't match, so `requestTyped`
 * can't be used for this op as-is. This hook calls the underlying
 * `WsClient.request<TResult>()` generic directly (the escape hatch
 * `requestTyped`'s own doc comment names) and validates the reply against
 * `fileGetResult` at runtime instead. Flagged for the WS-FILES/WS-EDITOR
 * contract owners to reconcile in `op-contracts.ts` (should read
 * `{ request: FileGetInput; response: FileGetResult }`).
 */
export function useFileDownloadUrl(fileId: string | null): UseFileDownloadUrlResult {
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected' && fileId !== null;

  const query = useQuery<string>({
    queryKey: ['file', 'downloadUrl', fileId],
    queryFn: async () => {
      const raw = await wsClient.request('file.get', { fileId });
      return fileGetResult.parse(raw).downloadUrl;
    },
    enabled,
  });

  return {
    downloadUrl: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
